import type { AgendaMeeting } from '../../hooks/useAgenda';
import type { Sector } from '../../types';

/** Monday of the ISO week for a given date string (YYYY-MM-DD). Reschedules
 *  push a meeting's date to Tue–Fri of the same week; chunks stay anchored to
 *  the Monday, so every meeting_date we surface from the agenda needs to be
 *  normalized back to its Monday before being used as a chunk's week_of. */
function mondayOfWeek(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const dow = d.getDay(); // 0=Sun..6=Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

/** Return the Monday of the next agenda meeting on which `sectorId` will
 *  appear, or null if we can't determine one from the supplied window.
 *
 *  - Active sectors appear as touches on every non-blackout meeting, so the
 *    answer is the first non-blackout meeting in the window.
 *  - Monitoring/emerging/other sectors only appear where a persisted slot
 *    references them — we scan for the earliest such slot.
 *  - If nothing matches, fall back to the first non-blackout meeting (better
 *    than nothing; matches the "next Monday" default we had before).
 */
export function nextMeetingForSector(
  meetings: AgendaMeeting[] | undefined,
  sectorId: number,
  sector: Sector | undefined,
): string | null {
  if (!meetings || meetings.length === 0) return null;

  const nonBlackout = meetings.filter(
    (m) => m.exception?.exception_type !== 'blackout',
  );
  if (nonBlackout.length === 0) return null;

  if (sector?.stage === 'active') {
    return mondayOfWeek(nonBlackout[0].meeting_date);
  }

  for (const m of nonBlackout) {
    const hit = m.slots.find((s) => s.sector_id === sectorId);
    if (hit) return mondayOfWeek(m.meeting_date);
  }

  return mondayOfWeek(nonBlackout[0].meeting_date);
}
